import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../api";
import { ErrorNote, Loading, secsToHHMM } from "../components/ui";
import { PeriodFilters } from "../components/PeriodFilters";
import { BarSeries, TrendLine } from "../components/charts3";
import { useLocations } from "../hooks";
import type { ExecutiveOverview, Filters, FullRankRow, GroupStat, LocationIntel, Rankings } from "../types";

type SortKey = "present_days" | "total_duration" | "avg_duration" | "name";

function StatTable({ title, rows }: { title: string; rows: GroupStat[] }) {
  return (
    <div className="panel">
      <h2>{title}</h2>
      <table>
        <thead>
          <tr><th>{title.includes("Location") ? "Location" : "Department"}</th><th>Employees</th>
            <th>Days</th><th>Total inside</th><th>Avg daily</th><th>Avg arrival</th><th>Avg exit</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td style={{ textTransform: "capitalize" }}>{r.key}</td>
              <td>{r.employees}</td>
              <td>{r.total_days}</td>
              <td>{r.total_inside_hhmm}</td>
              <td>{secsToHHMM(r.avg_daily_seconds)}</td>
              <td>{r.avg_arrival ?? "—"}</td>
              <td>{r.avg_exit ?? "—"}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="notice">No data.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function Locations() {
  const locations = useLocations();
  const [filters, setFilters] = useState<Filters>({});
  const [intel, setIntel] = useState<LocationIntel | null>(null);
  const [ov, setOv] = useState<ExecutiveOverview | null>(null);
  const [ranks, setRanks] = useState<FullRankRow[]>([]);
  const [sort, setSort] = useState<SortKey>("present_days");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const pageSize = 25;

  useEffect(() => {
    setLoading(true);
    Promise.all([api.locationIntel(filters), api.rankingsFull(filters, sort, order), api.overview(filters)])
      .then(([i, r, o]: [LocationIntel, Rankings, ExecutiveOverview]) => {
        setIntel(i); setRanks(r.full ?? []); setOv(o); setError(null);
      })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  }, [filters, sort, order]);

  const sortBtn = (key: SortKey, label: string) => (
    <button className={sort === key ? "primary" : "ghost"}
            onClick={() => { if (sort === key) setOrder(order === "desc" ? "asc" : "desc"); else { setSort(key); setOrder("desc"); } setPage(1); }}>
      {label}{sort === key ? (order === "desc" ? " ↓" : " ↑") : ""}
    </button>
  );

  const paged = ranks.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.max(1, Math.ceil(ranks.length / pageSize));

  return (
    <div>
      <h1 className="h1">Location & Employee Intelligence</h1>
      <p className="sub">Compare locations and departments, and browse the full employee ranking.</p>
      <PeriodFilters locations={locations} initialMode="month" storageKey="locations" onChange={setFilters} />

      {loading && <Loading what="intelligence" />}
      {error && <ErrorNote message={error} />}

      {intel && !loading && (
        <>
          {ov && (
            <div className="chart-grid">
              <div className="panel" style={{ marginTop: 0 }}>
                <h2>Monthly inside-office hours</h2>
                <TrendLine data={ov.trends.monthly} xKey="period" unit="hours" kind="area"
                           lines={[{ key: "inside_seconds", name: "Inside hours" }]} />
              </div>
              <div className="panel" style={{ marginTop: 0 }}>
                <h2>Location comparison</h2>
                <BarSeries xKey="label" barKey="total_inside_seconds" name="Inside hours" unit="hours"
                           data={intel.locations.map((l) => ({ label: l.key, total_inside_seconds: l.total_inside_seconds }))} />
              </div>
            </div>
          )}
          <StatTable title="By Location" rows={intel.locations} />
          <StatTable title="By Department" rows={intel.departments} />

          <div className="panel">
            <div className="row" style={{ justifyContent: "space-between" }}>
              <h2 style={{ margin: 0 }}>Full employee ranking ({ranks.length})</h2>
              <div className="row">
                Sort: {sortBtn("present_days", "Days")} {sortBtn("total_duration", "Total")}
                {sortBtn("avg_duration", "Avg")} {sortBtn("name", "Name")}
              </div>
            </div>
            <table>
              <thead>
                <tr><th>#</th><th>Employee</th><th>ID</th><th>Dept</th><th>Days</th>
                  <th>Total inside</th><th>Avg daily</th><th>Incomplete</th></tr>
              </thead>
              <tbody>
                {paged.map((r, i) => (
                  <tr key={r.employee_id}>
                    <td>{(page - 1) * pageSize + i + 1}</td>
                    <td><Link to={`/employees/${encodeURIComponent(r.employee_id)}`}>{r.employee_name || "—"}</Link></td>
                    <td><Link to={`/employees/${encodeURIComponent(r.employee_id)}`}>{r.employee_id}</Link></td>
                    <td>{r.department}</td>
                    <td>{r.present_days}</td>
                    <td>{r.total_inside_hhmm}</td>
                    <td>{secsToHHMM(r.avg_daily_seconds)}</td>
                    <td>{r.incomplete_days || ""}</td>
                  </tr>
                ))}
                {ranks.length === 0 && <tr><td colSpan={8} className="notice">No data.</td></tr>}
              </tbody>
            </table>
            <div className="pager">
              <button className="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
              <span className="notice">Page {page} / {totalPages}</span>
              <button className="ghost" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
